import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, CircularProgress, Divider, Link, Stack, Typography } from '@mui/material'
import { isEmail } from 'validator'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AuthProvider, getAdditionalUserInfo, signInWithPopup } from 'firebase/auth'
import { auth, googleProvider, microsoftProvider } from '../../firebaseConfig'
import { setToken } from '../../utils/auth'
import SsoProviderButtons from '../../components/Auth/SsoProviderButtons'
import { isAxiosNetworkError, isAxiosRateLimited, isAxiosServerError } from '../../utils/axiosUtils'
import {
    createCheckoutSession,
    createSsoCheckoutSession,
    getSignupErrorDetail,
    isSignupEnabled,
    parsePlan,
    sendSignupCode,
    verifySignupCode,
} from '../../services/auth/signupSession'
import AuthShell from '../../components/Auth/AuthShell'
import { loadTagManager } from '../../utils/tagManager'
import EmailStep from './EmailStep'
import CodeStep from './CodeStep'
import DetailsStep from './DetailsStep'
import SsoNameStep from './SsoNameStep'

const MIN_PASSWORD_LENGTH = 8
const CODE_LENGTH = 6
const RESEND_COOLDOWN_S = 60

// Email OTP proves inbox ownership; name + password are collected here but the account
// itself isn't created until POST /signup/complete confirms Stripe Checkout succeeded
// (see SignupComplete.tsx, which handles the Stripe return). No account exists yet at
// any point in this component.
type Phase = 'enter' | 'code' | 'details' | 'sso-name' | 'redirecting'

type FieldName = 'email' | 'code' | 'nameGiven' | 'nameFamily' | 'password'

// A discriminated union, not four independent booleans/values: those could disagree — a
// stale rate-limit error surviving alongside a freshly set field error, say, rendering
// two error lines at once — since each one had to be cleared by hand. One state, one
// setter, and that whole class of bug just can't happen here.
type SignupError =
    | { kind: 'field'; fields: FieldName[]; message: string }
    | { kind: 'account_exists' }
    | { kind: 'generic' }
    | { kind: 'rate_limited' }

function isAccountExistsResponse(error: unknown): boolean {
    return getSignupErrorDetail(error) === 'account_exists'
}

// Google's raw OAuth profile uses given_name/family_name; Microsoft/Graph uses
// givenName/surname — the two providers don't share a field naming convention.
export function guessNameFromSsoSignIn(
    displayName: string | null,
    profile: Record<string, unknown> | undefined
): { given: string; family: string } {
    const given =
        (typeof profile?.given_name === 'string' && profile.given_name) ||
        (typeof profile?.givenName === 'string' && profile.givenName) ||
        ''
    const family =
        (typeof profile?.family_name === 'string' && profile.family_name) ||
        (typeof profile?.surname === 'string' && profile.surname) ||
        ''
    if (given || family) return { given, family }

    // Neither provider profile had a split name — fall back to splitting displayName,
    // which Firebase always syncs from the provider onto the User object itself.
    const [firstPart, ...rest] = (displayName ?? '').trim().split(' ').filter(Boolean)
    return { given: firstPart ?? '', family: rest.join(' ') }
}

const Signup: React.FC = () => {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const [phase, setPhase] = useState<Phase>('enter')
    const [email, setEmail] = useState('')
    const [code, setCode] = useState('')
    const [signupToken, setSignupToken] = useState('')
    const [nameGiven, setNameGiven] = useState('')
    const [nameFamily, setNameFamily] = useState('')
    const [password, setPassword] = useState('')
    // Captured once, right after signInWithPopup resolves — this page renders outside <App/>,
    // so there's no populated auth store/api.ts interceptor to lean on for the authenticated
    // call below (see signupSession.ts's createSsoCheckoutSession).
    const [ssoIdToken, setSsoIdToken] = useState('')
    const [error, setError] = useState<SignupError | null>(null)
    const [searchParams] = useSearchParams()
    const checkoutCancelled = searchParams.get('checkout') === 'cancelled'
    // MD-1655: which of the 3 marketing tiers this visit came in for, read once at mount
    // like checkoutCancelled above — the marketing site (a separate WordPress property)
    // links here with ?plan=light|professional|premium; parsePlan falls back to
    // professional for anything else, so an old link with no plan param at all keeps
    // getting exactly what it always got.
    const plan = parsePlan(searchParams.get('plan'))
    const [submitting, setSubmitting] = useState(false)
    const [resendDeadline, setResendDeadline] = useState(0)
    const [now, setNow] = useState(() => Date.now())
    // null = still checking: better a brief spinner than a flash of a form that would 503.
    const [signupEnabled, setSignupEnabled] = useState<boolean | null>(null)
    // setSubmitting(true) doesn't disable the submit button until React commits — a second
    // submit event (double-click, or Enter fired twice) reaching this handler before then
    // would create a second, real Stripe Checkout Session for the same signup token.
    // Checked and set right here, inline, so both submits see it before either commits.
    const checkoutInFlightRef = useRef(false)
    // Same reasoning as checkoutInFlightRef, for the SSO provider buttons: disabled={submitting}
    // doesn't take effect until React commits either, so a fast double-click can still fire a
    // second signInWithPopup before the first one's setSubmitting(true) is visible.
    const ssoSignInInFlightRef = useRef(false)
    // True while restartFromEmail's own signOut() of an abandoned session is still pending.
    // signOut() and signInWithPopup both mutate the same global Firebase auth singleton, so a
    // new sign-in started in this window could have its session signed back out once the
    // older, unrelated signOut() call finally resolves — kept as state, not a ref, since it
    // has to actually disable the provider buttons while it's true.
    const [abandonedSsoCleanupPending, setAbandonedSsoCleanupPending] = useState(false)

    useEffect(loadTagManager, [])

    useEffect(() => {
        let ignore = false
        isSignupEnabled().then((enabled) => {
            if (!ignore) setSignupEnabled(enabled)
        })
        return () => {
            ignore = true
        }
    }, [])

    // Ticks resendSecondsLeft down to 0, then stops itself — checked inside the interval rather
    // than via the effect's dependency array, since resendDeadline never changes on its own once
    // the cooldown expires, which would otherwise leave the interval running indefinitely.
    useEffect(() => {
        if (phase !== 'code' || resendDeadline < Date.now()) return
        const interval = setInterval(() => {
            const currentTime = Date.now()
            setNow(currentTime)
            if (currentTime >= resendDeadline) clearInterval(interval)
        }, 1000)
        return () => clearInterval(interval)
    }, [phase, resendDeadline])

    const fieldErrorMessage = (name: FieldName): string | undefined =>
        error?.kind === 'field' && error.fields.includes(name) ? error.message : undefined

    const withClearedError =
        <T,>(setter: (value: T) => void) =>
        (value: T) => {
            setter(value)
            setError(null)
        }

    const handleSendCode = async () => {
        const trimmedEmail = email.trim()
        if (!isEmail(trimmedEmail)) {
            setError({ kind: 'field', fields: ['email'], message: t('signup.invalid_email') })
            return
        }
        setError(null)
        setSubmitting(true)
        try {
            await sendSignupCode(trimmedEmail, i18n.language.split('-')[0])
            // Re-pinned to what the code was actually sent to, not just left as whatever the
            // live `email` state now holds — the field stays editable during this request, so
            // without this, an edit made while it's in flight would carry into handleVerifyCode
            // as an email the OTP was never issued for.
            setEmail(trimmedEmail)
            setCode('')
            setPhase('code')
            const sentAt = Date.now()
            setNow(sentAt)
            setResendDeadline(sentAt + RESEND_COOLDOWN_S * 1000)
        } catch (err) {
            if (isAccountExistsResponse(err)) {
                setError({ kind: 'account_exists' })
            } else if (isAxiosRateLimited(err)) {
                setError({ kind: 'rate_limited' })
            } else {
                console.warn('[Signup] Failed to send verification code:', err)
                setError({ kind: 'generic' })
            }
        } finally {
            setSubmitting(false)
        }
    }

    const handleVerifyCode = async () => {
        if (code.length !== CODE_LENGTH) {
            setError({ kind: 'field', fields: ['code'], message: t('signup.invalid_code') })
            return
        }

        setError(null)
        setSubmitting(true)
        try {
            const token = await verifySignupCode(email.trim(), code.trim())
            setSignupToken(token)
            setPhase('details')
        } catch (err) {
            console.warn('[Signup] Code verification failed:', err)
            if (isAxiosRateLimited(err)) {
                // Not a wrong guess — reporting it as one would send someone straight into
                // more wrong guesses during the exact window this limit exists to slow down.
                setError({ kind: 'rate_limited' })
            } else if (isAxiosNetworkError(err) || isAxiosServerError(err)) {
                setError({ kind: 'generic' })
            } else {
                setError({ kind: 'field', fields: ['code'], message: t('signup.invalid_code') })
            }
        } finally {
            setSubmitting(false)
        }
    }

    const restartFromEmail = () => {
        // SSO's signInWithPopup already created a real, persisted Firebase session — clearing
        // this component's own state alone would leave that session live. useFirebaseTokenListener
        // treats any Google/Microsoft session as authenticated regardless of whether a backend
        // account exists for it, so without this, abandoning here and later just reloading the
        // app would silently sign the user in against nothing.
        if (ssoIdToken) {
            // Entry actions stay disabled (see abandonedSsoCleanupPending's own guards below)
            // until this settles — a new signInWithPopup started while it's still pending could
            // have its own, brand-new session signed back out once this older call resolves.
            setAbandonedSsoCleanupPending(true)
            auth.signOut()
                .catch((error) => console.error('[Signup] Failed to sign out of the abandoned SSO session:', error))
                .finally(() => setAbandonedSsoCleanupPending(false))
        }
        setPhase('enter')
        setCode('')
        setSignupToken('')
        setNameGiven('')
        setNameFamily('')
        setPassword('')
        setSsoIdToken('')
        setError(null)
    }

    const handleStartCheckout = async () => {
        const trimmedGiven = nameGiven.trim()
        const trimmedFamily = nameFamily.trim()
        if (!trimmedGiven || !trimmedFamily) {
            const missing: FieldName[] = []
            if (!trimmedGiven) missing.push('nameGiven')
            if (!trimmedFamily) missing.push('nameFamily')
            setError({ kind: 'field', fields: missing, message: t('signup.name_required') })
            return
        }
        if (password.length < MIN_PASSWORD_LENGTH) {
            setError({
                kind: 'field',
                fields: ['password'],
                message: t('signup.weak_password', { min: MIN_PASSWORD_LENGTH }),
            })
            return
        }

        if (checkoutInFlightRef.current) return
        checkoutInFlightRef.current = true

        setError(null)
        setSubmitting(true)
        try {
            const url = await createCheckoutSession(
                signupToken,
                password,
                {
                    name_given: trimmedGiven,
                    name_family: trimmedFamily,
                },
                plan
            )
            setPhase('redirecting')
            window.location.href = url
        } catch (err) {
            if (isAccountExistsResponse(err)) {
                setError({ kind: 'account_exists' })
            } else if (getSignupErrorDetail(err) === 'invalid_token') {
                // The email proof expired while filling the form — restart from the email
                // step, clearing the now-dead token and everything typed into this step.
                // Order matters: restartFromEmail() itself clears the error, so it has to
                // run before setting this one, not after.
                restartFromEmail()
                setError({ kind: 'generic' })
            } else if (isAxiosRateLimited(err)) {
                setError({ kind: 'rate_limited' })
            } else {
                console.warn('[Signup] Failed to start checkout:', err)
                setError({ kind: 'generic' })
            }
        } finally {
            setSubmitting(false)
            checkoutInFlightRef.current = false
        }
    }

    const handleSsoSignIn = async (provider: AuthProvider) => {
        if (ssoSignInInFlightRef.current || abandonedSsoCleanupPending) return
        ssoSignInInFlightRef.current = true

        setError(null)
        setSubmitting(true)
        // signInWithPopup itself is what actually persists the Firebase session — everything
        // after it (getIdToken, profile/name extraction) is local bookkeeping that can still
        // fail on its own. Tracked separately from ssoIdToken, which isn't set until all of
        // that bookkeeping has already succeeded, so it can't tell the catch below whether a
        // session was created by a failed attempt.
        let ssoSessionCreated = false
        try {
            const result = await signInWithPopup(auth, provider)
            ssoSessionCreated = true
            const idToken = await result.user.getIdToken()
            const profile = getAdditionalUserInfo(result)?.profile as Record<string, unknown> | undefined
            const { given, family } = guessNameFromSsoSignIn(result.user.displayName, profile)
            setSsoIdToken(idToken)
            setEmail(result.user.email ?? '')
            setNameGiven(given)
            setNameFamily(family)
            setPhase('sso-name')
        } catch (err) {
            // Includes the very common "user closed the popup" case — GoogleMicrosoftAuth.tsx
            // (the login equivalent of this) doesn't special-case that either today.
            console.warn('[Signup] SSO sign-in failed:', err)
            if (ssoSessionCreated) {
                // The popup itself succeeded, so a real session is live even though this
                // attempt is being reported as failed — without this, that session survives
                // with no signup ever having completed, and a later reload could silently
                // authenticate it (same class of gap restartFromEmail and the
                // account_exists_inactive branch already guard against elsewhere).
                try {
                    await auth.signOut()
                } catch (signOutError) {
                    console.error('[Signup] Failed to sign out after an incomplete SSO sign-in:', signOutError)
                }
            }
            setError({ kind: 'generic' })
        } finally {
            setSubmitting(false)
            ssoSignInInFlightRef.current = false
        }
    }

    const handleSsoStartCheckout = async () => {
        const trimmedGiven = nameGiven.trim()
        const trimmedFamily = nameFamily.trim()
        if (!trimmedGiven || !trimmedFamily) {
            const missing: FieldName[] = []
            if (!trimmedGiven) missing.push('nameGiven')
            if (!trimmedFamily) missing.push('nameFamily')
            setError({ kind: 'field', fields: missing, message: t('signup.name_required') })
            return
        }

        if (checkoutInFlightRef.current) return
        checkoutInFlightRef.current = true

        setError(null)
        setSubmitting(true)
        // Declared outside the try (but only ever assigned inside it) so the account_exists
        // branch below can reuse this same, already-fetched token instead of calling
        // getIdToken() a second time — a second call there would be unguarded inside a catch
        // block, and its rejection would escape this function entirely instead of being
        // handled as an error.
        let idToken = ssoIdToken
        try {
            // Refreshed here rather than reused from sign-in time — ssoIdToken can be over an
            // hour old by the time someone finishes this step, and Firebase ID tokens expire
            // in an hour. Falls back to the captured one only if the session has somehow
            // already gone away by now.
            idToken = (await auth.currentUser?.getIdToken()) ?? ssoIdToken
            const url = await createSsoCheckoutSession(
                idToken,
                {
                    name_given: trimmedGiven,
                    name_family: trimmedFamily,
                },
                plan
            )
            setPhase('redirecting')
            window.location.href = url
        } catch (err) {
            if (isAccountExistsResponse(err)) {
                // Not an error for SSO: the user is already fully authenticated via a real
                // Firebase session at this point, so the right move is to let them into the
                // app exactly as if they'd just logged in — reusing every bit of existing
                // state-handling machinery (App.tsx's gate, restrict_inactive_user's
                // download-only view, AccountDisabled) instead of inventing new
                // "you already have an account" UI for a case that, for SSO, isn't really
                // an error at all.
                setToken(idToken)
                navigate('/', { replace: true })
            } else if (getSignupErrorDetail(err) === 'account_exists_inactive') {
                // Unlike the case above, this account exists but isn't active (lapsed, or
                // never finished paying) — signing them in directly would land someone
                // attempting signup in a broken-looking inactive app with zero explanation,
                // having skipped Stripe entirely. Falls through to the same "you already
                // have an account, log in instead" messaging the password flow already
                // shows for its own account_exists case, rather than inventing new copy.
                // Fully restarts, not just signs out: ssoIdToken staying set would let a
                // resubmit fall back to this same, deliberately-terminated token once
                // auth.currentUser is gone (signOut() only clears the local session — the raw
                // token string here would otherwise still work until its own natural expiry).
                // restartFromEmail() itself clears the error, so it has to run before setting
                // this one, not after (same ordering as handleStartCheckout's invalid_token
                // case).
                restartFromEmail()
                setError({ kind: 'account_exists' })
            } else if (isAxiosRateLimited(err)) {
                // Deliberately left retryable in place, unlike the generic branch below: a
                // rate limit is transient by definition — it will succeed shortly — so there's
                // no reason to force a full Google/Microsoft re-consent just to try again.
                setError({ kind: 'rate_limited' })
            } else {
                console.warn('[Signup] Failed to start SSO checkout:', err)
                // Same reasoning as account_exists_inactive above: a generic failure here isn't
                // guaranteed transient, so this fully restarts rather than just signing out,
                // closing off the stale-token fallback a resubmit would otherwise still have.
                restartFromEmail()
                setError({ kind: 'generic' })
            }
        } finally {
            setSubmitting(false)
            checkoutInFlightRef.current = false
        }
    }

    const handlePhaseSubmit = () => {
        if (phase === 'enter') handleSendCode()
        else if (phase === 'code') handleVerifyCode()
        else if (phase === 'details') handleStartCheckout()
        else if (phase === 'sso-name') handleSsoStartCheckout()
    }

    const resendSecondsLeft = Math.max(0, Math.ceil((resendDeadline - now) / 1000))

    if (signupEnabled === null) {
        return (
            <AuthShell>
                <Stack justifyContent="center" alignItems="center" width={400} p={4}>
                    <CircularProgress />
                </Stack>
            </AuthShell>
        )
    }

    if (!signupEnabled) {
        return (
            <AuthShell>
                <Stack justifyContent="center" alignItems="stretch" width={400} p={4} spacing="16px">
                    <Typography variant="body2" align="center" color="text.secondary">
                        {t('signup.unavailable')}
                    </Typography>
                    <Typography variant="body2" align="center" color="text.secondary">
                        {t('signup.have_account')}{' '}
                        <Link component={RouterLink} to="/" variant="body2" sx={{ verticalAlign: 'baseline' }}>
                            {t('signup.go_to_login')}
                        </Link>
                    </Typography>
                </Stack>
            </AuthShell>
        )
    }

    return (
        <AuthShell>
            <Stack justifyContent="center" alignItems="stretch" width={400} p={2} spacing="12px">
                {/* Only on the initial state — mirrors Login's own headline, which hides once
                    the user has committed to a step (see Login.tsx) */}
                {phase === 'enter' && (
                    <Box>
                        <Typography
                            component="h1"
                            align="center"
                            sx={{ fontSize: '1.125rem', fontWeight: 600, mt: 0.5 }}
                        >
                            {t('signup.title')}
                        </Typography>
                        <Typography variant="body2" align="center" color="text.secondary">
                            {t('signup.subtitle')}
                        </Typography>
                    </Box>
                )}

                <Stack sx={{ p: 2 }}>
                    <Stack
                        component="form"
                        onSubmit={(e) => {
                            e.preventDefault()
                            handlePhaseSubmit()
                        }}
                    >
                        {phase === 'enter' && (
                            <>
                                {/* Mirrors Login.tsx's own ordering (GoogleMicrosoftAuth, then a
                                    divider, then the email form) for visual consistency between
                                    the two auth entry points — same shared button component too,
                                    so the two never drift apart in spacing/style again. */}
                                <Box sx={{ mb: 1 }}>
                                    <SsoProviderButtons
                                        onSelectGoogle={() => handleSsoSignIn(googleProvider)}
                                        onSelectMicrosoft={() => handleSsoSignIn(microsoftProvider)}
                                        disabled={submitting || abandonedSsoCleanupPending}
                                        mode="sign_up"
                                    />
                                </Box>
                                <Divider sx={{ my: 1 }}>{t('common.or')}</Divider>
                            </>
                        )}

                        {phase === 'enter' && (
                            <EmailStep
                                email={email}
                                onEmailChange={withClearedError(setEmail)}
                                errorMessage={fieldErrorMessage('email')}
                                submitting={submitting}
                            />
                        )}

                        {phase === 'sso-name' && (
                            <SsoNameStep
                                email={email.trim()}
                                nameGiven={nameGiven}
                                nameFamily={nameFamily}
                                onNameGivenChange={withClearedError(setNameGiven)}
                                onNameFamilyChange={withClearedError(setNameFamily)}
                                nameGivenErrorMessage={fieldErrorMessage('nameGiven')}
                                nameFamilyErrorMessage={fieldErrorMessage('nameFamily')}
                                submitting={submitting}
                            />
                        )}

                        {phase === 'code' && (
                            <CodeStep
                                email={email.trim()}
                                code={code}
                                codeLength={CODE_LENGTH}
                                onCodeChange={withClearedError(setCode)}
                                errorMessage={fieldErrorMessage('code')}
                                submitting={submitting}
                                resendSecondsLeft={resendSecondsLeft}
                                onResend={handleSendCode}
                            />
                        )}

                        {phase === 'details' && (
                            <DetailsStep
                                email={email.trim()}
                                nameGiven={nameGiven}
                                nameFamily={nameFamily}
                                password={password}
                                minPasswordLength={MIN_PASSWORD_LENGTH}
                                onNameGivenChange={withClearedError(setNameGiven)}
                                onNameFamilyChange={withClearedError(setNameFamily)}
                                onPasswordChange={withClearedError(setPassword)}
                                nameGivenErrorMessage={fieldErrorMessage('nameGiven')}
                                nameFamilyErrorMessage={fieldErrorMessage('nameFamily')}
                                passwordErrorMessage={fieldErrorMessage('password')}
                                submitting={submitting}
                            />
                        )}

                        {phase === 'redirecting' && (
                            <Typography variant="body2" align="center">
                                {t('signup.redirecting_to_checkout')}
                            </Typography>
                        )}

                        {phase !== 'enter' && phase !== 'redirecting' && (
                            <Button
                                type="button"
                                variant="text"
                                onClick={restartFromEmail}
                                disabled={submitting}
                                fullWidth
                            >
                                {t('signup.wrong_email')}
                            </Button>
                        )}
                    </Stack>

                    {/* Field errors render in their own field's helperText instead (MUI wires
                        up aria-describedby there) — this region is only for errors with no
                        single field to attach to. role="alert" announces it the moment it
                        appears, the same way a field's own helperText is announced via its
                        aria-describedby link when that field gets focus. */}
                    {error && error.kind !== 'field' && (
                        <Typography variant="subtitle2" color="error" align="center" role="alert">
                            {error.kind === 'account_exists' && t('signup.account_exists')}
                            {error.kind === 'generic' && t('signup.error_generic')}
                            {error.kind === 'rate_limited' && t('signup.rate_limited')}
                        </Typography>
                    )}
                    {/* Only on the initial state — the same reasoning as the title/subtitle
                        above, and once any real interaction sets an error it's served its
                        purpose. */}
                    {phase === 'enter' && checkoutCancelled && !error && (
                        <Typography variant="body2" align="center" color="text.secondary">
                            {t('signup.checkout_cancelled')}
                        </Typography>
                    )}

                    {/* Initial state, or whenever account_exists points the user at login —
                        mid code/details steps it would otherwise be a distraction, and could
                        abandon a live signup with no warning */}
                    {(phase === 'enter' || error?.kind === 'account_exists') && (
                        <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1 }}>
                            {t('signup.have_account')}{' '}
                            <Link component={RouterLink} to="/" variant="body2" sx={{ verticalAlign: 'baseline' }}>
                                {t('signup.go_to_login')}
                            </Link>
                        </Typography>
                    )}
                </Stack>
            </Stack>
        </AuthShell>
    )
}

export default Signup
